import { ThemeProvider } from '@mui/material';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { RemoveApplicationDialog } from '../components/removeApplicationDialog';

import api from 'api';
import { ClientApplicationStatus } from 'api/models/ClientApplicationStatus';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { loadSalesClients } from 'redux/sales-clients/sales-clients.middleware';
import type { AppDispatch, RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import { getErrorMessage } from 'utils/v2/errorHelper';

export interface RemoveApplicationConfirmationModalProps {
    open: boolean;
    mobile: boolean;
    toggleRemoveAppModal: () => void;
    clientView: boolean;
    closeMobileQuickActions?: () => void;
}

const readSelectedApplicationIds = (): number[] => {
    try {
        const stored = localStorage.getItem('selectedApps');
        const parsed: unknown = stored ? JSON.parse(stored) : [];
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.filter((id): id is number => typeof id === 'number');
    } catch {
        return [];
    }
};

const RemoveApplicationConfirmationModal: FC<RemoveApplicationConfirmationModalProps> = ({
    open,
    mobile,
    toggleRemoveAppModal,
    clientView,
    closeMobileQuickActions,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { translate } = useTranslate();
    const { entity: activeApp } = useSelector((state: RootState) => state.application);

    const [loading, setLoading] = useState(false);

    const selectedApps = useMemo(() => {
        if (!open) {
            return [];
        }
        return readSelectedApplicationIds();
    }, [open]);

    useEffect(() => {
        if (!open) {
            setLoading(false);
        }
    }, [open]);

    if (!open) {
        return null;
    }

    const handleClose = () => {
        if (loading) {
            return;
        }
        toggleRemoveAppModal();
    };

    const handleConfirm = async () => {
        if (loading) {
            return;
        }

        let applicationIds = selectedApps;
        if (clientView) {
            applicationIds = activeApp?.id != null ? [activeApp.id] : [];
        }

        if (!applicationIds.length) {
            return;
        }

        setLoading(true);

        try {
            await Promise.all(
                applicationIds.map((id) =>
                    api.application.setClientApplicationStatus(id.toString(), {
                        client_status: ClientApplicationStatus.REMOVED,
                    })
                )
            );

            if (!clientView) {
                localStorage.setItem('selectedApps', JSON.stringify([]));
            }

            dispatch(loadSalesClients({}));
            dispatch(
                setAlert({
                    message:
                        applicationIds.length > 1
                            ? translate('casesRemovedSuccessfully').replace(
                                  '{{count}}',
                                  String(applicationIds.length)
                              )
                            : translate('caseRemovedSuccessfully'),
                    type: 'success',
                })
            );

            toggleRemoveAppModal();
            if (mobile) {
                closeMobileQuickActions?.();
            }
            if (clientView) {
                navigate('/dashboard');
            }
        } catch (error) {
            setLoading(false);
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    return (
        <ThemeProvider theme={appThemeV2}>
            <RemoveApplicationDialog
                clientView={clientView}
                selectedApps={selectedApps}
                loading={loading}
                onClose={handleClose}
                onConfirm={handleConfirm}
            />
        </ThemeProvider>
    );
};

export default RemoveApplicationConfirmationModal;
